import { useState } from "react";
import { SendHorizonal } from "lucide-react";
import { SectionHead } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/scroll";
import { trackCall } from "@/lib/track";

export const Calculator = ({ onSendEstimate }) => {
  const [length, setLength] = useState("200");
  const [spacing, setSpacing] = useState("8");
  const [rails, setRails] = useState("2");

  const L = parseFloat(length);
  const S = parseFloat(spacing);
  const R = parseInt(rails, 10);
  const valid = L > 0 && S > 0 && R > 0;

  const posts = valid ? Math.ceil(L / S) + 1 : null;
  const brackets = valid ? posts * R : null;
  const screws = valid ? brackets * 6 : null;
  const railFeet = valid ? L * R : null;

  const fmt = (n) => (n === null ? "—" : n.toLocaleString("en-US"));

  const tiles = [
    ["Posts", posts, "calc-posts"],
    ["Brackets needed", brackets, "calc-brackets"],
    ["Screws (minimum)", screws, "calc-screws"],
    ["Rail footage", railFeet === null ? null : railFeet, "calc-rail-feet"],
  ];

  const sendEstimate = () => {
    if (!valid) return;
    const text =
      `Calculator estimate — ${L} ft run, posts ${S} ft apart, ${R} rail${R === 1 ? "" : "s"}: ` +
      `${posts} posts, ${brackets} brackets, ${screws} screws minimum, ${railFeet} ft of rail.`;
    onSendEstimate(text);
    scrollToId("quote");
  };

  return (
    <section className="section" id="calculator" data-testid="calculator-section">
      <div className="wrap">
        <SectionHead num="06" eyebrow="Parts count" title="How many do I need?" />
        <Reveal>
          <div className="calc-card">
            <div className="calc-inputs">
              <div className="field">
                <label htmlFor="calc-length">Total fence length (feet)</label>
                <input
                  id="calc-length"
                  data-testid="calc-length-input"
                  type="number"
                  min="1"
                  inputMode="decimal"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="calc-spacing">Post spacing (feet)</label>
                <input
                  id="calc-spacing"
                  data-testid="calc-spacing-input"
                  type="number"
                  min="1"
                  inputMode="decimal"
                  value={spacing}
                  onChange={(e) => setSpacing(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="calc-rails">Number of rails</label>
                <input
                  id="calc-rails"
                  data-testid="calc-rails-input"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={rails}
                  onChange={(e) => setRails(e.target.value)}
                />
              </div>
            </div>

            <div className="calc-results" aria-live="polite">
              {tiles.map(([label, value, tid]) => (
                <div className="calc-tile" key={label}>
                  <div className="v tnum" data-testid={tid}>
                    {fmt(value)}
                    {label === "Rail footage" && value !== null ? " ft" : ""}
                  </div>
                  <div className="l">{label}</div>
                </div>
              ))}
            </div>

            <p className="calc-note" data-testid="calc-disclaimer">
              An estimate for a straight run. Corners, gate posts, ends and grade changes will change your count. Call{" "}
              <a href="tel:+18666526569" onClick={() => trackCall("calculator")} style={{ color: "var(--blue)", fontWeight: 600 }}>866-652-6569</a> and we will walk your layout with you.
            </p>

            <button
              className="btn btn-outline"
              style={{ marginTop: 24 }}
              onClick={sendEstimate}
              disabled={!valid}
              data-testid="calc-send-estimate-btn"
            >
              <SendHorizonal size={16} aria-hidden="true" />
              Send this estimate with my quote request
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
